import { useState, useCallback, useRef, useEffect } from 'react';
import {
  sendPlansChatMessage,
  sendAnonymousChatMessage,
  getItineraryConversation,
  getPlansConversation,
  undoPlan,
  approveProposal,
  rejectProposal,
  PlansChatType,
  PlansChatApiResponse,
  PlansChatApiError,
  PlansChatEntryIntent,
  type PlanChange,
  type PlanProposal,
} from '../services/plansChatApi';
import { useUserStore } from '../stores';
import { trackEvent } from './useAnalytics';
import {
  currentWebAccountSession, isCurrentWebAccountSession, useWebAccountSession,
  type WebAccountSession,
} from '../utils/accountSession';
import { newUuid } from '../utils/uuid';

function genId(prefix: string): string {
  return `${prefix}-${newUuid()}`;
}

export type { PlanChange };

export interface PlanChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  /**
   * Set on the reply that committed an edit. The confirmation says how many
   * changes landed; this says which ones, so the reader does not have to leave
   * the conversation to find out what happened.
   */
  changes?: PlanChange[];
  /**
   * Set on a reply that STAGED a change proposal instead of applying it.
   * The card renders by hydration (the reference is all the envelope
   * carries), and the message hosts the Approve/Reject surface.
   */
  proposalId?: number;
  /**
   * Set on a reply whose plan build failed on our side and that resending the
   * same message can fix (the server's `build_failed.retryable`).
   */
  retryable?: boolean;
}

export interface UsePlanChatReturn {
  messages: PlanChatMessage[];
  isSending: boolean;
  conversationId: string | null;
  /**
   * The id of the entry this chat was opened with — the conversation's
   * identity before the server mints a conversation (an anonymous chat never
   * gets one). The save bar compares it with `PENDING_PLAN_OWNER_KEY`. Absent
   * or null when the chat has no entry.
   */
  entryId?: string | null;
  itineraryId: number | null;
  itineraryData: Record<string, unknown> | null;
  showUndo: boolean;
  softGate: { active: boolean; type: string };
  error: string | null;

  sendMessage: (text: string, chatType?: PlansChatType) => Promise<void>;
  undo: () => Promise<{ deleted: boolean }>;
  reset: (entry?: PlanChatEntry) => void;
  /**
   * Approve or reject a staged proposal. Returns the terminal proposal so
   * the card can render its outcome; appends the outcome copy to the
   * conversation and refreshes the plan when the change applied.
   */
  resolveProposal: (
    proposalId: number,
    decision: 'approve' | 'reject',
  ) => Promise<PlanProposal>;

  messagesEndRef: React.RefObject<HTMLDivElement>;

  /**
   * The latest turn failed on our side and nothing is sending: the last reply
   * carries `build_failed.retryable`, or the last send ended in a timeout, a
   * dead connection or a 5xx. Never for a protocol 4xx or a turn the server is
   * still processing. Optional only so hand-built fixtures of this type need
   * not name it; the hook always returns both.
   */
  canRetry?: boolean;
  /** Resend the last user message through `sendMessage`. */
  retryLastMessage?: () => void;
}

export type PlanChatEntry = {
  intent: PlansChatEntryIntent;
  entryId: string;
  query?: string;
  itineraryId?: number;
  /** The entry has received its first successful response and may hydrate its own conversation. */
  started?: boolean;
  /**
   * The conversation a started new-plan entry belongs to. The entry lives in
   * sessionStorage, one per tab, so it owns the id; the per-user localStorage
   * slot is shared across tabs and is only the fallback for an entry stored
   * by an older build without this field.
   */
  conversationId?: string | null;
  /** Where this entry came from, for `plan_created` (§7). Absent means chat. */
  source?: 'capture';
};

/**
 * `plan_created.source` for a signed-in creation (§7). The entry may come
 * back from sessionStorage cast, not parsed, so only the one known value
 * maps to `capture`; anything else is chat.
 */
const planCreatedSource = (entry: PlanChatEntry | null | undefined): 'capture' | 'ai_chat' => (
  entry?.source === 'capture' ? 'capture' : 'ai_chat'
);

/**
 * Where a chat's conversation id is remembered.
 *
 * Scoped by itinerary, because one key per user is not enough. The backend
 * rebinds a resumed conversation to whatever plan it is linked to, so an
 * itinerary-scoped chat writing into the discovery slot makes the next
 * discovery send resume a plan-linked conversation and edit that plan instead
 * of building a new one. Mobile guards this by refusing to persist at all;
 * scoping the key also gives a plan's chat continuity across sessions, which
 * the transcript restore below needs.
 */
const conversationStorageKey = (
  userId: number | null, itineraryId?: number | null,
) => (
  `vizday_plans_chat_convo_v1:${userId ?? 'anon'}`
  + (itineraryId != null ? `:itinerary:${itineraryId}` : '')
);
export const ANON_ENTRY_STORAGE_KEY = 'vizday_anon_plan_chat_entry_id';
/**
 * Which conversation built the stored anonymous pending plan: the entry id of
 * the chat whose send stored `vizday_pending_plan`. A sibling of that key, not
 * part of it — the pending plan's shape and everything the signup
 * claim/resolve reads are unchanged. Written with every pending-plan write
 * (removed when the writing chat has no entry) and removed by `useUserStore`
 * wherever it removes the plan.
 */
export const PENDING_PLAN_OWNER_KEY = 'vizday_pending_plan_owner';
export const PLAN_CHAT_ENTRY_STORAGE_KEY = 'vizday_plan_chat_entry_v1';
export const planChatEntryStorageKey = (userId: number | null) => (
  `${PLAN_CHAT_ENTRY_STORAGE_KEY}:${userId ?? 'anon'}`
);
const pendingTurnStorageKey = (entryId: string, session: WebAccountSession) => (
  `vizday_plan_chat_turn_v1:${session.userId ?? 'anon'}:${entryId}`
);
type PendingTurnRequest = {
  message: string;
  conversationId: string | null;
  itineraryId: number | null;
  chatType: PlansChatType;
  entryIntent?: PlansChatEntryIntent;
  entryId?: string;
  expectedRevision: number | null;
};
type PendingTurn = { key: string; id: string; request?: PendingTurnRequest };
const isPendingTurnRequest = (value: unknown): value is PendingTurnRequest => {
  if (!value || typeof value !== 'object') return false;
  const request = value as Partial<PendingTurnRequest>;
  return typeof request.message === 'string'
    && (request.conversationId === null || typeof request.conversationId === 'string')
    && (request.itineraryId === null || typeof request.itineraryId === 'number')
    && ['plan_create', 'plan_edit', 'on_trip', 'quick_question'].includes(
      request.chatType as string,
    )
    && (request.entryIntent === undefined || [
      'new_plan', 'resume_discovery', 'edit_itinerary',
    ].includes(request.entryIntent))
    && (request.entryId === undefined || typeof request.entryId === 'string')
    && (request.expectedRevision === null
      || typeof request.expectedRevision === 'number');
};
const readPendingTurn = (
  entryId?: string, session = currentWebAccountSession(),
): PendingTurn | null => {
  if (!entryId) return null;
  try {
    const raw = sessionStorage.getItem(pendingTurnStorageKey(entryId, session));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      key?: unknown; id?: unknown; request?: unknown;
    };
    if (typeof parsed.key !== 'string' || typeof parsed.id !== 'string') return null;
    return {
      key: parsed.key,
      id: parsed.id,
      ...(isPendingTurnRequest(parsed.request) ? { request: parsed.request } : {}),
    };
  } catch { return null; }
};
const readStoredEntry = (
  itineraryId?: number | null,
  userId = currentWebAccountSession().userId,
): PlanChatEntry | null => {
  try {
    const raw = sessionStorage.getItem(
      planChatEntryStorageKey(userId),
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PlanChatEntry>;
    if (typeof parsed.entryId !== 'string') return null;
    if (parsed.intent === 'edit_itinerary') {
      return parsed.itineraryId === itineraryId ? parsed as PlanChatEntry : null;
    }
    if (
      itineraryId == null
      && (parsed.intent === 'new_plan' || parsed.intent === 'resume_discovery')
    ) return parsed as PlanChatEntry;
  } catch { /* invalid recovery envelope */ }
  return null;
};
/**
 * Persist an entry directly, independent of React. The first build often
 * resolves after the hook unmounted, when a state set (and the effect that
 * would persist it) is a no-op; the response path calls this so the entry's
 * progression survives into the next mount.
 */
const writeStoredEntry = (entry: PlanChatEntry, userId: number | null) => {
  try {
    sessionStorage.setItem(planChatEntryStorageKey(userId), JSON.stringify(entry));
  } catch { /* */ }
};
/**
 * Whether the tab's stored entry for this user is still the entry `entryId`.
 * One record per user per tab: a newer hook instance may have replaced it
 * (`reset({...})`) or removed it (the chat was closed) since a request left.
 * An absent or unreadable record is not ownership.
 */
const storedEntryIs = (entryId: string, userId: number | null): boolean => {
  try {
    const raw = sessionStorage.getItem(planChatEntryStorageKey(userId));
    if (!raw) return false;
    return (JSON.parse(raw) as Partial<PlanChatEntry> | null)?.entryId === entryId;
  } catch { return false; }
};
/**
 * The response path's write: a compare-and-swap on the stored entry. A
 * response that resolves after its instance unmounted must not bring back a
 * chat the user closed, or replace the entry a newer instance stored. The
 * mounted owner's write (the effect on `entry`) stays unconditional.
 */
const writeStoredEntryIfOwned = (entry: PlanChatEntry, userId: number | null): boolean => {
  if (!storedEntryIs(entry.entryId, userId)) return false;
  writeStoredEntry(entry, userId);
  return true;
};

/**
 * User-facing copy for every protocol code the plans-chat endpoint can return.
 *
 * The endpoint answers with stable codes on purpose; the reader must never see
 * one. Anything absent here falls back to a generic sentence rather than the
 * code itself, so a NEW code degrades to something readable instead of leaking.
 */
const PROTOCOL_COPY: Record<string, string> = {
  // Shipped in #115 and covered by its test; kept verbatim rather than
  // rewording a string users already see.
  idempotency_mismatch:
    'This edit session was refreshed. Check your plan, then send the change '
    + 'again if it was not applied.',
  turn_in_progress:
    "I'm still working on your last message. Give it a moment.",
  turn_superseded:
    "I'm still working on your last message. Give it a moment.",
  plan_version_conflict:
    'Your plan changed while that was sending, so I left it alone. Take a look '
    + 'and send the change again if you still want it.',
  intent_conflict:
    'This chat belongs to a different plan. Start a new chat for this one.',
  conversation_subject_mismatch:
    'This chat belongs to a different plan. Start a new chat for this one.',
  itinerary_required: 'Open a plan first and I can change it from there.',
  message_limit_reached:
    'Create a free account to keep planning — it takes a moment.',
  chat_failed:
    'Something went wrong on my side. Give it another try in a moment.',
};

/**
 * Recovery retry cadence, in milliseconds, then every RECOVERY_STEADY_DELAY_MS.
 *
 * The schedule MUST outlast the server's 3-minute lease. Every retry taken
 * while the lease is live answers 409, and those answers do NOT renew it —
 * only a reclaim does — so a schedule that gives up before expiry strands the
 * turn on an attempt whose client is already gone. 5 + 10 + 20 + 40 + 60 puts
 * an attempt at t=135s and the next at t=195s, past the 180s lease. That one
 * is not a poll: the server reclaims the turn and re-runs the build inside
 * that request, so its answer is the real result.
 */
export const RECOVERY_RETRY_DELAYS_MS = [5000, 10000, 20000, 40000];
export const RECOVERY_STEADY_DELAY_MS = 60000;
/** Absolute horizon from the first unresolved answer of a visibility episode. */
export const RECOVERY_HORIZON_MS = 240000;

/**
 * Ceiling on the authoritative re-fetch.
 *
 * Recovery holds the in-flight slot until this GET settles, so an unbounded
 * one strands the turn for the whole session: later triggers see "already
 * recovering" and no retry is ever armed. A bounded failure reaches the retry
 * path instead, which is the behaviour every other unresolved answer gets.
 */
export const RECONCILE_TIMEOUT_MS = 20000;

/**
 * Re-attach the recovered turn's own metadata after an authoritative replace.
 *
 * The server transcript is role and content only, so change chips and a
 * proposal card would vanish in the replace.
 *
 * Attribution is by CONTENT, and content is not an identifier: identical
 * assistant texts are legal ("Done."), and the transcript carries no message
 * ids to disambiguate them. So the rule is unambiguity — decorate only when
 * exactly one assistant message bears the recovered text. Scanning for the
 * last match does not avoid mis-attribution, it causes it: a newer turn made
 * elsewhere would be handed this older turn's proposal id. Ambiguity costs a
 * chip; a wrong card is a lie about which change was staged.
 *
 * The retry flag (`build_failed.retryable`) is the one exception, because it
 * is a claim about the LATEST turn, not about a message: it goes on the
 * transcript's last message, and only when that is an assistant reply bearing
 * the recovered text. The failure copy is a fixed sentence, so two failed
 * turns legitimately repeat it — the uniqueness rule would drop the flag
 * exactly when the user most needs the button. An older copy never gets it,
 * and a newer turn made elsewhere (a different reply at the tail) means the
 * recovered turn is not the latest, so nothing is flagged.
 */
function decorateRecoveredTurn(
  restored: PlanChatMessage[],
  inner: PlansChatApiResponse['response'],
): PlanChatMessage[] {
  const tail = restored.length - 1;
  const flagged = inner.build_failed?.retryable === true
    && tail >= 0
    && restored[tail].role === 'assistant'
    && restored[tail].content === inner.text
    ? restored.map((message, i) => (i === tail ? { ...message, retryable: true } : message))
    : restored;
  const report = inner.mutation_report;
  const changes = report?.committed ? report.operations : undefined;
  const proposalId = inner.proposal_ref?.id;
  if (!changes?.length && !proposalId) return flagged;
  const target = flagged.findIndex(
    (m) => m.role === 'assistant' && m.content === inner.text,
  );
  if (target === -1) return flagged;
  const ambiguous = flagged.some(
    (m, i) => i !== target && m.role === 'assistant' && m.content === inner.text,
  );
  if (ambiguous) return flagged;
  return flagged.map((message, i) => (i === target ? {
    ...message,
    ...(changes?.length ? { changes } : {}),
    ...(proposalId ? { proposalId } : {}),
  } : message));
}

/** Codes that mean the plan on screen may be stale. */
const PLAN_CHANGED_CODES = new Set([
  'idempotency_mismatch', 'plan_version_conflict',
]);

export function usePlanChat(
  itineraryId?: number | null,
  defaultChatType: PlansChatType = 'plan_create',
  initialContentRevision?: number | null,
  initialEntry?: PlanChatEntry | null,
  /**
   * Called when a conflict means the plan on screen may no longer match the
   * server. The copy tells the reader to check their plan, so something has to
   * actually refresh it — generateStoryForPlan already models this.
   */
  onPlanChanged?: () => void,
): UsePlanChatReturn {
  const startingEntry = initialEntry ?? readStoredEntry(itineraryId);
  // Rehydrate the saved transcript so a conversation survives signup (the
  // anonymous session persists it; useUserStore keeps it alongside the
  // resolved conversation id).
  const [messages, setMessages] = useState<PlanChatMessage[]>(() => {
    if (startingEntry?.intent === 'new_plan' && !startingEntry.started) return [];
    try {
      const raw = localStorage.getItem('vizday_plans_chat_messages');
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Array<{ role: 'user' | 'assistant'; content: string }>;
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(m => (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string')
        .map(m => ({
          id: genId(m.role),
          role: m.role,
          content: m.content,
          timestamp: new Date().toISOString(),
        }));
    } catch {
      return [];
    }
  });
  const [isSending, setIsSending] = useState(false);
  // Claimed synchronously, because the `isSending` state read at the top of
  // sendMessage is stale inside this closure and the hydration await below
  // leaves a window in which the UI is still sendable.
  // A monotonic token, not a boolean. "Still mine?" cannot be a comparison of
  // scope strings: those come back around on an A -> B -> A move, at which
  // point an older send's finally would clear a NEWER send's claim and let
  // recovery run beside a live request. Only the send that took the token can
  // give it back.
  const sendSeqRef = useRef(0);
  const isSendingRef = useRef<number | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [entry, setEntry] = useState<PlanChatEntry | null>(startingEntry);
  const autoSentEntryRef = useRef<string | null>(null);
  const pendingTurnRef = useRef<PendingTurn | null>(
    readPendingTurn(startingEntry?.entryId),
  );
  // Turn-recovery controller, scoped to this receipt's surface. Every armed
  // retry is dropped on logout, account switch, entry or plan change,
  // unmount, terminal resolution, and any user send that supersedes it — a
  // stale timer must never POST an old account's stored envelope under the
  // current credentials.
  const recoveryEpochRef = useRef(0);
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The epoch that claimed the in-flight slot, or null. Epoch-scoped rather
  // than boolean: a cancelled request keeps running until its socket closes,
  // and a plain flag would make the NEW scope's one automatic trigger see
  // "already recovering" and return — stranding its receipt until the next
  // visibility change. The old request's result is dropped by the guard anyway.
  const recoveryInFlightRef = useRef<number | null>(null);
  const recoveryStateRef = useRef<{ step: number; deadlineAt: number | null }>(
    { step: 0, deadlineAt: null },
  );
  const attemptRecoveryRef = useRef<(() => void) | null>(null);
  const startRecoveryEpisodeRef = useRef<(() => void) | null>(null);
  // Turn ids whose non-transcript effects have already been applied in THIS
  // session. Receipt deletion can fail — storage quota, a private-mode throw —
  // and a receipt that outlives its own resolution replays `plan_created` on
  // the next trigger. This closes the in-session repeat; the durable one is
  // recorded in the plan's §9 as at-least-once.
  const appliedTurnsRef = useRef<Set<string>>(new Set());

  const [conversationId, setConversationId] = useState<string | null>(() => {
    if (startingEntry?.intent === 'new_plan') {
      if (!startingEntry.started) return null;
      // The entry's own id first: the slot below is one per user across tabs,
      // and another tab's build may have overwritten it since.
      if (typeof startingEntry.conversationId === 'string') return startingEntry.conversationId;
    }
    try {
      return localStorage.getItem(
        conversationStorageKey(currentWebAccountSession().userId, itineraryId),
      ) || null;
    } catch {
      return null;
    }
  });
  const [currentItineraryId, setCurrentItineraryId] = useState<number | null>(itineraryId ?? null);
  const user = useUserStore((s) => s.user);
  const accountSession = useWebAccountSession();
  const isAuthenticated = !!user;
  const [anonymousToken, setAnonymousToken] = useState<string | null>(() => {
    try { return localStorage.getItem('vizday_anon_token'); } catch { return null; }
  });

  // Sync itineraryId prop changes (for draft resume)
  useEffect(() => {
    setCurrentItineraryId(itineraryId ?? null);
  }, [itineraryId]);

  const [itineraryData, setItineraryData] = useState<Record<string, unknown> | null>(null);
  const [showUndo, setShowUndo] = useState(false);
  const [softGate, setSoftGate] = useState<{ active: boolean; type: string }>({ active: false, type: '' });
  const [error, setError] = useState<string | null>(null);
  // The last send ended in a failure a resend can fix (decision 8).
  const [lastSendFailed, setLastSendFailed] = useState(false);
  const scopeKey = `${accountSession.generation}:${itineraryId ?? 'discovery'}:${entry?.entryId ?? 'resume'}`;
  const scopeRef = useRef(scopeKey);
  const previousScopeRef = useRef(scopeKey);
  const stateScopeRef = useRef(scopeKey);
  const entryOwnerRef = useRef(accountSession.userId);
  scopeRef.current = scopeKey;

  // Persist the anonymous conversation so signup can restore it
  // (useUserStore reads vizday_plans_chat_messages when resolving the session).
  //
  // The key is unowned: any later mount, under any account, reads it. So only
  // a transcript owned by the CURRENT account session may be written there.
  // `messages` belongs to `stateScopeRef`'s scope, which carries the
  // account-session generation. The render that first sees a logout
  // (`isAuthenticated === false`) still holds the previous account's
  // transcript under the previous generation's scope — the return below
  // already hides it from the screen for that reason, and this write refuses
  // it for the same one, instead of trusting the scope reset further down to
  // clear it before anything reads the key.
  useEffect(() => {
    if (isAuthenticated || messages.length === 0) return;
    if (stateScopeRef.current !== scopeKey) return;
    try {
      localStorage.setItem(
        'vizday_plans_chat_messages',
        JSON.stringify(messages.map(m => ({ role: m.role, content: m.content }))),
      );
    } catch { /* */ }
  }, [messages, isAuthenticated, scopeKey]);

  /** Drop the armed retry and start the next episode from the first delay. */
  const resetRecoverySchedule = useCallback(() => {
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
    recoveryStateRef.current = { step: 0, deadlineAt: null };
  }, []);

  /**
   * Reset AND orphan anything already in flight.
   *
   * The epoch bump is what makes cancellation total: clearTimeout cannot
   * recall a callback already queued, and no POST can be recalled at all, so
   * their results are dropped rather than applied to a surface, an account or
   * a plan that has since changed.
   */
  const cancelRecovery = useCallback(() => {
    recoveryEpochRef.current += 1;
    resetRecoverySchedule();
  }, [resetRecoverySchedule]);

  const recoveryGuard = useCallback(() => {
    const requestedSession = currentWebAccountSession();
    const requestedScope = scopeRef.current;
    const epoch = recoveryEpochRef.current;
    return () => (
      isCurrentWebAccountSession(requestedSession)
      && scopeRef.current === requestedScope
      && recoveryEpochRef.current === epoch
    );
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null!);
  // Freshest conversation id for reads after an await, where the captured
  // closure value is stale by construction — hydration may have assigned one
  // while this send was waiting for it.
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  // In-flight transcript hydration. A send fired before it resolves awaits it,
  // so the turn continues this plan's conversation instead of minting a fresh
  // one and orphaning the restored transcript. Mobile has always done this.
  const hydrationRef = useRef<Promise<void> | null>(null);
  const onPlanChangedRef = useRef(onPlanChanged);
  onPlanChangedRef.current = onPlanChanged;
  // Freshest entry for recovery, which runs from a timer and a document
  // listener rather than from a render that captured one.
  const entryRef = useRef(entry);
  entryRef.current = entry;

  const sendMessage = useCallback(async (text: string, chatType?: PlansChatType) => {
    if (!text.trim() || isSendingRef.current !== null || isSending) return;
    const sendToken = ++sendSeqRef.current;
    isSendingRef.current = sendToken;
    setIsSending(true);
    setLastSendFailed(false);
    // A user-initiated send owns the turn from here. Anything recovery has
    // armed or already sent is superseded — never concurrent with it.
    cancelRecovery();
    const requestedSession = currentWebAccountSession();
    const requestedScope = scopeRef.current;
    const stillCurrent = () => (
      isCurrentWebAccountSession(requestedSession)
      && scopeRef.current === requestedScope
      // The token, not just the scope: scope strings come back around on
      // an A -> B -> A move, and without this an older send's response
      // would pass the guard and clear a newer send's receipt.
      && isSendingRef.current === sendToken
    );

    if (hydrationRef.current) {
      try { await hydrationRef.current; } catch { /* resume failed; send fresh */ }
      if (!stillCurrent()) {
        if (isSendingRef.current === sendToken) isSendingRef.current = null;
        return;
      }
    }

    const turnKey = `${entry?.entryId ?? 'legacy'}:${currentItineraryId ?? 'new'}:${text.trim()}`;
    const retryingTurn = isAuthenticated && pendingTurnRef.current?.key === turnKey;
    const optimisticTurnExists = retryingTurn && messagesRef.current.some(
      (existing) => existing.role === 'user' && existing.content === text.trim(),
    );
    const userMsg: PlanChatMessage = {
      id: genId('user'),
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString(),
    };
    if (!optimisticTurnExists) setMessages(prev => [...prev, userMsg]);
    setError(null);
    setShowUndo(false);

    try {
      if (isAuthenticated) {
        // Newest of the two, not "the response's if we have one". After a
        // commit both are set: the response's revision, and the reloaded
        // plan's. Those agree for our own edit, but a concurrent write
        // elsewhere makes the reloaded one newer — preferring the response
        // would then send a revision the screen has already moved past and
        // earn a 409 for a plan the reader can see is current.
        //
        // This is for a NEW turn only. A retry replays the stored envelope,
        // so its revision is fixed at first delivery and unaffected by this.
        const responseRevision = typeof itineraryData?.content_revision === 'number'
          ? itineraryData.content_revision : null;
        const loadedRevision = initialContentRevision ?? null;
        const expectedRevision = responseRevision === null ? loadedRevision
          : loadedRevision === null ? responseRevision
          : Math.max(responseRevision, loadedRevision);
        const currentRequest: PendingTurnRequest = {
          message: text.trim(),
          // After the hydration await above, the captured value is stale.
          conversationId: conversationIdRef.current,
          itineraryId: entry?.intent === 'new_plan' ? null : currentItineraryId,
          chatType: chatType || defaultChatType,
          entryIntent: entry?.intent,
          entryId: entry?.entryId,
          expectedRevision,
        };
        const pendingTurn = pendingTurnRef.current?.key === turnKey
          ? pendingTurnRef.current
          : { key: turnKey, id: newUuid(), request: currentRequest };
        // New receipts retain their exact canonical request envelope. A retry
        // after reload must replay these bytes with the same UUID; rebuilding
        // from newly hydrated state causes an idempotency mismatch and can
        // tempt an unsafe fresh delivery after an already-committed response.
        if (!pendingTurn.request) pendingTurn.request = currentRequest;
        pendingTurnRef.current = pendingTurn;
        if (entry?.entryId) {
          try {
            sessionStorage.setItem(
              pendingTurnStorageKey(entry.entryId, requestedSession),
              JSON.stringify(pendingTurn),
            );
          } catch { /* */ }
        }
        // Authenticated path — use plans-chat endpoint
        const request = pendingTurn.request;
        const response: PlansChatApiResponse = await sendPlansChatMessage(
          request.message, request.conversationId, request.itineraryId,
          request.chatType, request.entryIntent, request.entryId, pendingTurn.id,
          request.expectedRevision,
        );
        if (!stillCurrent()) return;
        pendingTurnRef.current = null;
        if (entry?.entryId) {
          try {
            sessionStorage.removeItem(
              pendingTurnStorageKey(entry.entryId, requestedSession),
            );
          } catch { /* */ }
        }

        const newConvoId = response.conversation_id;
        setConversationId(newConvoId);
        try {
          localStorage.setItem(
            conversationStorageKey(requestedSession.userId, itineraryId), newConvoId,
          );
          localStorage.removeItem(ANON_ENTRY_STORAGE_KEY);
          // The rehydrated signup transcript is now stale relative to this
          // conversation — drop it so later mounts don't show old history.
          localStorage.removeItem('vizday_plans_chat_messages');
        } catch {}

        // Record it here too, not only in recovery: if the durable receipt
        // removal above failed, a later scope change re-hydrates this turn and
        // recovery would count it a second time.
        appliedTurnsRef.current.add(pendingTurn.id);
        if (response.response.itinerary_id) {
          // The backend says explicitly when a plan was created this turn —
          // itinerary_id presence alone also covers edits of existing plans
          // (funnel step: land → first plan).
          if (response.response.created) {
            trackEvent('plan_created', {
              source: planCreatedSource(entry),
              anonymous: false,
              itinerary_id: response.response.itinerary_id,
            });
          }
          setCurrentItineraryId(response.response.itinerary_id);
        }
        // Written to storage here, not only through the effect on `entry`:
        // when this resolves after unmount the state set is a no-op and the
        // effect never runs, and the next mount would auto-send the query
        // again under a fresh turn id (idempotency_mismatch).
        if (
          entry?.intent === 'new_plan'
          && (!entry.started || entry.conversationId !== newConvoId)
        ) {
          const progressed: PlanChatEntry = { ...entry, started: true, conversationId: newConvoId };
          setEntry(progressed);
          writeStoredEntryIfOwned(progressed, requestedSession.userId);
        }
        if (response.response.itinerary) setItineraryData(response.response.itinerary);
        setShowUndo(response.response.showUndo);

        const gateType = response.gate || (response.soft_gate ? (response.gate_type || 'upgrade') : null);
        if (gateType) setSoftGate({ active: true, type: gateType });

        const report = response.response.mutation_report;
        const committedChanges = report?.committed ? report.operations : undefined;
        const proposalRef = response.response.proposal_ref;
        const assistantMsg: PlanChatMessage = {
          id: genId('assistant'),
          role: 'assistant',
          content: response.response.text,
          timestamp: new Date().toISOString(),
          ...(committedChanges?.length ? { changes: committedChanges } : {}),
          ...(proposalRef ? { proposalId: proposalRef.id } : {}),
          ...(response.response.build_failed?.retryable ? { retryable: true } : {}),
        };
        setMessages(prev => [...prev, assistantMsg]);
        // The plan on screen is now behind the conversation. Telling the reader
        // an edit landed while the plan visibly has not is the worse half of
        // the problem the confirmation was meant to solve.
        if (report?.committed) onPlanChangedRef.current?.();
      } else {
        // Anonymous path — use session endpoint
        if (entry?.entryId) {
          try { localStorage.setItem(ANON_ENTRY_STORAGE_KEY, entry.entryId); } catch { /* */ }
        }
        const prevMessages = messages.map(m => ({ role: m.role, content: m.content }));
        const response = await sendAnonymousChatMessage(
          text.trim(),
          prevMessages,
          anonymousToken,
        );
        if (!stillCurrent()) return;
        // Both persisted directly, for the same reason as the signed-in path:
        // after unmount neither the entry effect nor the transcript effect
        // runs, and the next mount would re-send the query to an endpoint
        // with no idempotency key (a second build, a spent message).
        // Both only while the tab's stored entry is still this one: after a
        // close or a newer entry, the late reply is not that chat's history.
        // An instance with no entry has no stored identity to compare.
        const ownsStoredEntry = !entry
          || storedEntryIs(entry.entryId, requestedSession.userId);
        if (entry?.intent === 'new_plan' && !entry.started) {
          const progressed: PlanChatEntry = { ...entry, started: true };
          setEntry(progressed);
          if (ownsStoredEntry) writeStoredEntry(progressed, requestedSession.userId);
        }
        if (ownsStoredEntry) {
          try {
            localStorage.setItem('vizday_plans_chat_messages', JSON.stringify([
              ...prevMessages,
              { role: 'user', content: text.trim() },
              { role: 'assistant', content: response.text },
            ]));
          } catch { /* */ }
        }

        // Store anonymous token
        if (response.anonymous_token) {
          setAnonymousToken(response.anonymous_token);
          try { localStorage.setItem('vizday_anon_token', response.anonymous_token); } catch {}
        }

        // Check gate — use 'signup' which PlanChatOverlay/ChatSoftGate understand
        if (response.gate === 'auth_required') {
          setSoftGate({ active: true, type: 'signup' });
        }

        // Store pending plan if built. The anonymous service returns
        // pending_plan only on handoff turns; a revision handoff replaces the
        // same session's plan, so count plan_created once per anon session.
        // A dedicated marker (not the pending-plan key itself) survives
        // remounts and is cleared on reset() and post-signup resolution, so
        // a genuinely new anon session counts again.
        if (response.pending_plan) {
          let alreadyTracked = false;
          try { alreadyTracked = localStorage.getItem('vizday_anon_plan_tracked') != null; } catch {}
          if (!alreadyTracked) {
            trackEvent('plan_created', { source: 'ai_chat', anonymous: true });
            try { localStorage.setItem('vizday_anon_plan_tracked', '1'); } catch {}
          }
          try {
            localStorage.setItem('vizday_pending_plan', JSON.stringify(response.pending_plan));
            // After the plan write, in the same try: an owner is recorded only
            // for a plan that was actually stored. A chat with no entry owns
            // nothing, so an earlier conversation's owner must not claim it.
            if (entry?.entryId) localStorage.setItem(PENDING_PLAN_OWNER_KEY, entry.entryId);
            else localStorage.removeItem(PENDING_PLAN_OWNER_KEY);
          } catch {}
        }

        const assistantMsg: PlanChatMessage = {
          id: genId('assistant'),
          role: 'assistant',
          content: response.text,
          timestamp: new Date().toISOString(),
          ...(response.build_failed?.retryable ? { retryable: true } : {}),
        };
        setMessages(prev => [...prev, assistantMsg]);
      }
    } catch (err) {
      if (!stillCurrent()) return;
      const aborted = err instanceof Error && err.name === 'AbortError';
      const stillProcessing = err instanceof Error
        && (err.message === 'turn_in_progress' || err.message === 'turn_superseded');
      const terminalResponse = err instanceof PlansChatApiError
        && err.status >= 400 && err.status < 500 && !stillProcessing;
      if (terminalResponse) {
        pendingTurnRef.current = null;
        if (entry?.entryId) {
          try {
            sessionStorage.removeItem(
              pendingTurnStorageKey(entry.entryId, requestedSession),
            );
          } catch { /* */ }
        }
      }
      // Protocol codes are for the client, not the reader. Every one of these
      // used to reach the transcript verbatim — `turn_in_progress` rendered as
      // an assistant message is not an error report, it is a leak.
      // The anonymous signup gate is a 403 whose body carries `gate`, not
      // `error`, so it must be recognised before the code table — otherwise
      // the funnel's own gate reads as a failure. Mobile already guards this.
      if (err instanceof PlansChatApiError && err.body?.gate === 'auth_required') {
        setSoftGate({ active: true, type: 'signup' });
        setIsSending(false);
        return;
      }
      // Not a protocol answer and not still running: a timeout, a dead
      // connection or a 5xx — the one-tap retry can resend it (decision 8).
      if (!stillProcessing
        && !(err instanceof PlansChatApiError && err.status >= 400 && err.status < 500)) {
        setLastSendFailed(true);
      }
      const code = err instanceof PlansChatApiError ? err.message : '';
      if (PLAN_CHANGED_CODES.has(code)) onPlanChangedRef.current?.();
      const serverReason = err instanceof PlansChatApiError
        && code === 'tier_limit' && typeof err.body?.message === 'string'
        ? err.body.message as string : undefined;
      if (stillProcessing) {
        // The copy says the turn is still being worked on. Something has to
        // make that true — without this nothing asks again until the tab is
        // hidden and shown.
        startRecoveryEpisodeRef.current?.();
      }
      const message = aborted
        ? "That took longer than it should. Send your message again and I'll pick up where we left off."
        : serverReason
        ?? PROTOCOL_COPY[code]
        ?? (err instanceof PlansChatApiError
          ? 'Something went wrong on my side. Give it another try in a moment.'
          : err instanceof Error ? err.message : 'Something went wrong');
      setError(message);
    } finally {
      // Only if this send still owns the surface. A scope change already
      // released the flag on its way out, and a newer send may hold it now —
      // clearing it unconditionally would hand recovery a green light to run
      // concurrently with that send. Mobile has always guarded this.
      if (stillCurrent()) {
        if (isSendingRef.current === sendToken) isSendingRef.current = null;
        setIsSending(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSending, conversationId, currentItineraryId, defaultChatType, entry, itineraryData, initialContentRevision, accountSession.generation]);

  // Restore this plan's own transcript. The endpoint has existed since the
  // in-plan chat was built but only mobile called it, so every "Edit with AI"
  // opened blank while the server-side conversation continued underneath.
  //
  // Only ever fills an EMPTY transcript: a hydration that lands after the user
  // has typed must not replace their optimistic turn or an in-flight reply.
  useEffect(() => {
    if (!isAuthenticated || itineraryId == null) {
      // No hydration for this scope — a send here must not await the previous
      // scope's request, which has no timeout of its own.
      hydrationRef.current = null;
      return;
    }
    let cancelled = false;
    const requestedSession = currentWebAccountSession();
    const requestedScope = scopeRef.current;
    hydrationRef.current = getItineraryConversation(itineraryId).then((detail) => {
      if (cancelled || !detail) return;
      if (!isCurrentWebAccountSession(requestedSession)) return;
      if (scopeRef.current !== requestedScope) return;
      // The id and the transcript are ONE decision. Adopting the id while
      // keeping a transcript that came from somewhere else shows the reader
      // conversation A while their sends continue conversation B — and an
      // optimistic turn already on screen must keep its own conversation.
      //
      // Decided from the ref rather than inside an updater, so the updater
      // stays pure and the id is visible to a send resuming from the await
      // below before React has re-rendered.
      if (messagesRef.current.length > 0) return;
      conversationIdRef.current = detail.id;
      setConversationId(detail.id);
      setMessages(detail.messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({
          id: genId(m.role),
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: new Date().toISOString(),
        })));
    }).catch(() => { /* no resumable conversation; the first send mints one */ })
      .finally(() => {
        if (scopeRef.current === requestedScope) hydrationRef.current = null;
      });
    return () => { cancelled = true; hydrationRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryId, isAuthenticated, scopeKey, accountSession.generation]);

  useEffect(() => {
    if (!entry || entryOwnerRef.current !== accountSession.userId) return;
    writeStoredEntry(entry, accountSession.userId);
  }, [entry, accountSession.userId]);

  useEffect(() => {
    if (entryOwnerRef.current === accountSession.userId) return;
    entryOwnerRef.current = accountSession.userId;
    setEntry(readStoredEntry(itineraryId, accountSession.userId));
    autoSentEntryRef.current = null;
  }, [accountSession.userId, itineraryId]);

  useEffect(() => {
    if (previousScopeRef.current === scopeKey) return;
    previousScopeRef.current = scopeKey;
    stateScopeRef.current = scopeKey;
    setMessages([]);
    setConversationId(null);
    setCurrentItineraryId(itineraryId ?? null);
    setItineraryData(null);
    setShowUndo(false);
    setError(null);
    setLastSendFailed(false);
    setIsSending(false);
    // The REF too, not just the state. It is a plain boolean shared across
    // scopes, so a send still open on the old surface would otherwise consume
    // the new surface's one automatic recovery trigger — and clearing it
    // early is safe, because that send's own guard drops its results.
    isSendingRef.current = null;
    // Re-read for the NEW surface rather than simply dropping it. Without
    // this the per-scope trigger below is dead code for every scope change
    // driven by the itineraryId prop or an account generation bump: the
    // receipt stays in sessionStorage, unreachable until a remount.
    pendingTurnRef.current = readPendingTurn(entryRef.current?.entryId);
    // A retry armed for the OLD surface would now POST under whatever account
    // and plan the new scope belongs to.
    cancelRecovery();
  }, [scopeKey, itineraryId, cancelRecovery]);

  // --- Turn recovery ------------------------------------------------------
  //
  // For a signed-in user the result of a lost delivery is not lost — only the
  // delivery is. The server persisted the turn and will replay it, and until
  // now no client ever asked. Recovery re-POSTs the STORED envelope under the
  // STORED turn id and lets the server's idempotency machinery answer.

  const scheduleRecoveryRetry = useCallback((stillCurrent: () => boolean) => {
    const state = recoveryStateRef.current;
    const now = Date.now();
    if (state.deadlineAt === null) state.deadlineAt = now + RECOVERY_HORIZON_MS;
    const delay = RECOVERY_RETRY_DELAYS_MS[state.step] ?? RECOVERY_STEADY_DELAY_MS;
    state.step += 1;
    if (now + delay > state.deadlineAt) {
      // Exhausted — but the receipt STAYS. The next page load or tab focus
      // opens a fresh episode against the same turn, and by then the lease has
      // certainly lapsed, so that episode's first POST reclaims and rebuilds.
      resetRecoverySchedule();
      setError(PROTOCOL_COPY.chat_failed);
      return;
    }
    // Every current caller clears first, so this is belt-and-braces — but a
    // future one that does not would orphan a live timer whose callback then
    // nulls the shared ref out from under its replacement.
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = setTimeout(() => {
      recoveryTimerRef.current = null;
      if (!stillCurrent()) return;
      attemptRecoveryRef.current?.();
    }, delay);
  }, [resetRecoverySchedule]);

  const attemptRecovery = useCallback(async () => {
    if (!isAuthenticated) return;
    const pending = pendingTurnRef.current;
    const entryId = entryRef.current?.entryId;
    if (!entryId) return;
    // Never concurrent with a user-initiated send of the same turn, and never
    // two recoveries at once.
    if (isSendingRef.current !== null) return;
    if (recoveryInFlightRef.current === recoveryEpochRef.current) return;
    if (recoveryTimerRef.current) return;
    // A receipt written by an older build carries no envelope. Rebuilding one
    // is exactly what the server rejects as an idempotency mismatch, so there
    // is nothing safe to replay here — leave it to the user's next send, which
    // rebuilds deliberately and mints its own turn.
    if (!pending?.request) return;
    // A receipt is bound to the surface that minted it. The storage key is
    // account + entry and carries no plan, so a receipt from another plan
    // must not be replayed here — its reply belongs to a different chat.
    if ((pending.request.itineraryId ?? null) !== (itineraryId ?? null)) return;

    const requestedSession = currentWebAccountSession();
    const stillCurrent = recoveryGuard();
    if (!stillCurrent()) return;

    const epoch = recoveryEpochRef.current;
    recoveryInFlightRef.current = epoch;
    // Set the moment the POST returns 2xx. After that the turn is decided and
    // NOTHING the reconciliation GET says can be a terminal answer about it —
    // a 404 or 401 from that GET must retry, never discard the receipt.
    let turnAnswered = false;
    try {
      const request = pending.request;
      const response = await sendPlansChatMessage(
        request.message, request.conversationId, request.itineraryId,
        request.chatType, request.entryIntent, request.entryId, pending.id,
        request.expectedRevision,
      );
      if (!stillCurrent()) return;
      turnAnswered = true;
      // The turn is decided: whatever the last send's failure said is now
      // superseded by this answer. A stale flag would offer "Try again" for a
      // turn the server already finished — and since the receipt is cleared
      // below, that tap would send a NEW turn. The recovered reply's own
      // `retryable` (decorateRecoveredTurn) is the only retry signal left.
      setLastSendFailed(false);

      // The server transcript is authoritative. Appending the recovered text
      // through the send path would duplicate it whenever hydration already
      // contains the assistant turn, and dedup by text is not available —
      // identical texts are legal, and proposal turns carry their own ids.
      const detail = await getPlansConversation(
        response.conversation_id, RECONCILE_TIMEOUT_MS,
      );
      if (!stillCurrent()) return;
      const inner = response.response;
      // A FULL replace. Web's own hydration only ever fills an EMPTY
      // transcript, so routing this through it would keep the stale local one
      // and the recovered result would never land.
      const restored: PlanChatMessage[] = detail.messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({
          id: genId(m.role),
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: new Date().toISOString(),
        }));
      const reconciled = decorateRecoveredTurn(restored, inner);
      messagesRef.current = reconciled;
      setMessages(reconciled);

      // Everything that is NOT the transcript gets the same treatment a live
      // 200 gets. Only the append is replaced by the authoritative re-fetch.
      const newConvoId = response.conversation_id;
      conversationIdRef.current = newConvoId;
      setConversationId(newConvoId);
      try {
        localStorage.setItem(
          conversationStorageKey(requestedSession.userId, itineraryId), newConvoId,
        );
        localStorage.removeItem(ANON_ENTRY_STORAGE_KEY);
        localStorage.removeItem('vizday_plans_chat_messages');
      } catch { /* */ }
      // The transcript replace above is idempotent — it is a whole-array
      // assignment from the server. These are not, so they run at most once
      // per turn id per session.
      const alreadyApplied = appliedTurnsRef.current.has(pending.id);
      appliedTurnsRef.current.add(pending.id);
      if (inner.itinerary_id) {
        if (inner.created && !alreadyApplied) {
          trackEvent('plan_created', {
            source: planCreatedSource(entryRef.current),
            anonymous: false,
            itinerary_id: inner.itinerary_id,
          });
        }
        setCurrentItineraryId(inner.itinerary_id);
      }
      const recoveredEntry = entryRef.current;
      if (
        recoveredEntry?.intent === 'new_plan'
        && (!recoveredEntry.started || recoveredEntry.conversationId !== newConvoId)
      ) {
        // The same record the live path writes, so a recovered entry does not
        // overwrite the stored one with a copy missing its conversation id.
        // Ownership-checked like the live path: a late turn_in_progress can
        // arm recovery after unmount, and by then a newer instance may have
        // closed this entry or replaced it.
        const progressed: PlanChatEntry = {
          ...recoveredEntry, started: true, conversationId: newConvoId,
        };
        setEntry(progressed);
        writeStoredEntryIfOwned(progressed, requestedSession.userId);
      }
      if (inner.itinerary) setItineraryData(inner.itinerary);
      setShowUndo(inner.showUndo);
      const gateType = response.gate
        || (response.soft_gate ? (response.gate_type || 'upgrade') : null);
      if (gateType) setSoftGate({ active: true, type: gateType });
      if (inner.mutation_report?.committed) onPlanChangedRef.current?.();
      setError(null);

      // The receipt goes LAST. If the re-fetch above had failed, nothing would
      // have been applied and the receipt would still be there for the next
      // attempt — where the server answers with a pure terminal replay and no
      // provider work. Clearing first would risk a stale transcript with no
      // way back, or a second plan_created event.
      pendingTurnRef.current = null;
      try {
        sessionStorage.removeItem(
          pendingTurnStorageKey(entryId, requestedSession),
        );
      } catch { /* */ }
      cancelRecovery();
    } catch (err) {
      if (!stillCurrent()) return;
      const code = err instanceof PlansChatApiError ? err.message : '';
      const stillProcessing = code === 'turn_in_progress'
        || code === 'turn_superseded';
      // 4xx that is not one of those two is the server's final answer —
      // including idempotency_mismatch, which must NEVER mint a fresh turn:
      // that is a second delivery of work the server may already have
      // committed.
      const terminal = !turnAnswered
        && err instanceof PlansChatApiError
        && err.status >= 400 && err.status < 500 && !stillProcessing;
      if (terminal) {
        // The server's final answer about the turn: nothing to resend.
        setLastSendFailed(false);
        pendingTurnRef.current = null;
        try {
          sessionStorage.removeItem(
            pendingTurnStorageKey(entryId, requestedSession),
          );
        } catch { /* */ }
        cancelRecovery();
        if (PLAN_CHANGED_CODES.has(code)) onPlanChangedRef.current?.();
        setError(PROTOCOL_COPY[code] ?? PROTOCOL_COPY.chat_failed);
        return;
      }
      // Everything else is unresolved, not failed: a 5xx leaves the turn
      // unfinished on purpose so its lease can lapse, and a timeout or a dead
      // socket says nothing at all. Keep the receipt and stay on the schedule.
      if (stillProcessing) {
        // The server has the turn and recovery is on it; a resend now would
        // only collide with it.
        setLastSendFailed(false);
        setError(PROTOCOL_COPY[code]);
      }
      scheduleRecoveryRetry(stillCurrent);
    } finally {
      // Release only if this attempt still owns the slot: a cancel has already
      // handed it to whatever came next.
      if (recoveryInFlightRef.current === epoch) recoveryInFlightRef.current = null;
    }
  }, [
    isAuthenticated, itineraryId, setConversationId, cancelRecovery,
    recoveryGuard, scheduleRecoveryRetry,
  ]);
  attemptRecoveryRef.current = () => { void attemptRecovery(); };
  startRecoveryEpisodeRef.current = () => {
    scheduleRecoveryRetry(recoveryGuard());
  };

  useEffect(() => {
    // (a) Page load, once per surface. This is web's cold start, and a reload
    // of an already-visible tab fires no visibility transition at all — a
    // hydrated receipt that is never consulted is the bug this exists to fix.
    attemptRecoveryRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, isAuthenticated]);

  useEffect(() => {
    // (b) Every hidden -> visible transition.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        attemptRecoveryRef.current?.();
        return;
      }
      // A schedule left running while the tab is hidden would fire its whole
      // backlog at once on return, and its horizon would be measured from a
      // moment the reader was not present for.
      resetRecoverySchedule();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [resetRecoverySchedule]);

  useEffect(() => () => { cancelRecovery(); }, [cancelRecovery]);

  useEffect(() => {
    if (
      entry?.intent !== 'new_plan'
      || entry.started
      || !entry.query?.trim()
      || autoSentEntryRef.current === entry.entryId
    ) return;
    autoSentEntryRef.current = entry.entryId;
    void sendMessage(entry.query);
  }, [entry, sendMessage]);

  const undo = useCallback(async (): Promise<{ deleted: boolean }> => {
    if (!currentItineraryId) return { deleted: false };
    const requestedSession = currentWebAccountSession();
    const requestedScope = scopeRef.current;
    const stillCurrent = () => (
      isCurrentWebAccountSession(requestedSession)
      && scopeRef.current === requestedScope
    );

    try {
      const revision = typeof itineraryData?.content_revision === 'number'
        ? itineraryData.content_revision : initialContentRevision ?? null;
      const result = await undoPlan(currentItineraryId, revision);
      if (!stillCurrent()) return { deleted: false };
      if (result.deleted) {
        setCurrentItineraryId(null);
        setItineraryData(null);
        setShowUndo(false);
        return { deleted: true };
      } else if (result.success) {
        setShowUndo(false);
      }
      return { deleted: false };
    } catch (err) {
      if (!stillCurrent()) return { deleted: false };
      // Same contract as send: the reader never sees a protocol code, and a
      // conflict means the plan on screen may be stale.
      const code = err instanceof PlansChatApiError ? err.message : '';
      if (PLAN_CHANGED_CODES.has(code)) onPlanChangedRef.current?.();
      setError(
        PROTOCOL_COPY[code]
        ?? (err instanceof PlansChatApiError
          ? 'Something went wrong on my side. Give it another try in a moment.'
          : err instanceof Error ? err.message : 'Undo failed'),
      );
      return { deleted: false };
    }
  }, [currentItineraryId, itineraryData, initialContentRevision]);

  const reset = useCallback((nextEntry?: PlanChatEntry) => {
    // Before the refs move. This function writes previousScopeRef too, so the
    // scope-change effect will see no transition and will not cancel for us —
    // and an A -> B -> A reset would otherwise make a timer armed under the
    // first A valid again, because the scope comparison is true once more.
    cancelRecovery();
    // Same reason as the scope effect: reset() moves surfaces too. Both the
    // ref AND the state: the send guard rejects on either, so releasing only
    // the ref leaves the new surface unable to send at all.
    isSendingRef.current = null;
    setIsSending(false);
    const nextScope = `${accountSession.generation}:${itineraryId ?? 'discovery'}:${nextEntry?.entryId ?? 'resume'}`;
    scopeRef.current = nextScope;
    previousScopeRef.current = nextScope;
    stateScopeRef.current = nextScope;
    setEntry(nextEntry ?? null);
    autoSentEntryRef.current = null;
    pendingTurnRef.current = readPendingTurn(nextEntry?.entryId);
    setMessages([]);
    setConversationId(null);
    setCurrentItineraryId(itineraryId ?? null);
    setItineraryData(null);
    setShowUndo(false);
    setError(null);
    setLastSendFailed(false);
    setSoftGate({ active: false, type: '' });
    try {
      localStorage.removeItem(
        conversationStorageKey(accountSession.userId, itineraryId),
      );
      localStorage.removeItem('vizday_plans_chat_messages');
      localStorage.removeItem('vizday_anon_plan_tracked');
      if (nextEntry) {
        sessionStorage.setItem(
          planChatEntryStorageKey(accountSession.userId), JSON.stringify(nextEntry),
        );
      } else {
        sessionStorage.removeItem(planChatEntryStorageKey(accountSession.userId));
      }
      if (nextEntry?.intent === 'new_plan') {
        localStorage.setItem(ANON_ENTRY_STORAGE_KEY, nextEntry.entryId);
      } else {
        localStorage.removeItem(ANON_ENTRY_STORAGE_KEY);
      }
    } catch { /* */ }
  }, [itineraryId, accountSession.generation, accountSession.userId, cancelRecovery]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const resolveProposal = useCallback(async (
    proposalId: number,
    decision: 'approve' | 'reject',
  ): Promise<PlanProposal> => {
    // Same guard every sibling async path takes (internal review): a
    // decision resolving after a reset, scope switch, or account change
    // must not append the OLD plan's outcome into the NEW transcript.
    const requestedSession = currentWebAccountSession();
    const requestedScope = scopeRef.current;
    const stillCurrent = () => (
      mountedRef.current
      && isCurrentWebAccountSession(requestedSession)
      && scopeRef.current === requestedScope
    );
    const result = decision === 'approve'
      ? await approveProposal(proposalId)
      : await rejectProposal(proposalId);
    if (!stillCurrent()) {
      // The card that asked is gone (unmount, reset, scope or account
      // switch); the server state is settled — return it and touch
      // NOTHING local, analytics included: a late event would attribute
      // this decision to whichever identity is active now (external r22).
      return result;
    }
    trackEvent('plan_proposal_decided', {
      decision,
      status: result.status,
    });
    // The outcome copy is server-authored (applied summary, conflict or
    // expiry copy); reject returns none because the status is the story —
    // mirror the transcript line the server already appended.
    const outcomeText = result.message
      || (result.status === 'rejected'
        ? 'No problem — I left your plan unchanged. Tell me what you\u2019d like instead.'
        : null);
    if (outcomeText) {
      setMessages(prev => [...prev, {
        id: genId('assistant'),
        role: 'assistant',
        content: outcomeText,
        timestamp: new Date().toISOString(),
        ...(result.status === 'applied'
          && result.mutation_report?.operations?.length
          ? { changes: result.mutation_report.operations }
          : {}),
      }]);
    }
    if (result.status === 'applied') {
      // The plan on screen is now behind the approved change.
      onPlanChangedRef.current?.();
    }
    return result;
  }, []);

  // The latest turn's outcome, read from the transcript: the last message
  // must be a reply the server flagged retryable. A later user message —
  // sent, failed with a 4xx, or in flight — means it is no longer the latest.
  const lastMessage = messages.length ? messages[messages.length - 1] : undefined;
  const canRetry = stateScopeRef.current === scopeKey
    // Load-bearing: between setIsSending(true) and the user bubble landing a
    // send awaits hydration, and the old retryable reply is still the tail.
    && !isSending
    && ((lastMessage?.role === 'assistant' && lastMessage.retryable === true)
      || lastSendFailed);
  const retryLastMessage = () => {
    if (!canRetry) return;
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUser) return;
    void sendMessage(lastUser.content);
  };

  return {
    messages: stateScopeRef.current === scopeKey ? messages : [],
    isSending: stateScopeRef.current === scopeKey ? isSending : false,
    conversationId: stateScopeRef.current === scopeKey ? conversationId : null,
    entryId: stateScopeRef.current === scopeKey ? (entry?.entryId ?? null) : null,
    itineraryId: stateScopeRef.current === scopeKey ? currentItineraryId : (itineraryId ?? null),
    itineraryData: stateScopeRef.current === scopeKey ? itineraryData : null,
    showUndo: stateScopeRef.current === scopeKey ? showUndo : false,
    softGate,
    error,
    sendMessage,
    undo,
    reset,
    resolveProposal,
    messagesEndRef,
    canRetry,
    retryLastMessage,
  };
}
